import { cn } from '@/lib/cn';

const TONES = {
  neutral: 'bg-ink-100 text-ink-700 dark:bg-ink-800 dark:text-ink-300',
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300',
  accent: 'bg-accent-50 text-accent-700 dark:bg-accent-500/15 dark:text-accent-400',
  success: 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-500',
  warning: 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-500',
  danger: 'bg-danger-50 text-danger-700 dark:bg-danger-500/15 dark:text-danger-500',
  info: 'bg-info-50 text-info-700 dark:bg-info-500/15 dark:text-info-500',
};

export const Badge = ({ tone = 'neutral', dot = false, size = 'md', className, children }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap',
      size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
      TONES[tone],
      className,
    )}
  >
    {dot && <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />}
    {children}
  </span>
);

/**
 * Peta status job/run ke label Indonesia beserta warnanya.
 *
 * Status "berjalan" diberi titik berdenyut - pengguna langsung tahu ada
 * sesuatu yang bergerak tanpa harus membaca teksnya.
 */
export const STATUS_META = {
  draft: { label: 'Draf', tone: 'neutral' },
  scheduled: { label: 'Terjadwal', tone: 'info' },
  queued: { label: 'Mengantre', tone: 'warning' },
  running: { label: 'Berjalan', tone: 'accent', pulse: true },
  completed: { label: 'Selesai', tone: 'success' },
  failed: { label: 'Gagal', tone: 'danger' },
  cancelled: { label: 'Dibatalkan', tone: 'neutral' },
  paused: { label: 'Dijeda', tone: 'neutral' },
  // Status laporan bug.
  open: { label: 'Terbuka', tone: 'warning' },
  triaged: { label: 'Ditriase', tone: 'info' },
  in_progress: { label: 'Dikerjakan', tone: 'accent', pulse: true },
  resolved: { label: 'Selesai', tone: 'success' },
  wont_fix: { label: 'Tidak Diperbaiki', tone: 'neutral' },
  duplicate: { label: 'Duplikat', tone: 'neutral' },

  active: { label: 'Aktif', tone: 'success' },
  inactive: { label: 'Nonaktif', tone: 'neutral' },
  blocked: { label: 'Diblokir', tone: 'danger' },
};

export const StatusBadge = ({ status, size = 'md', className }) => {
  const meta = STATUS_META[status] ?? { label: status, tone: 'neutral' };

  return (
    <Badge tone={meta.tone} size={size} className={className}>
      <span
        className={cn('size-1.5 rounded-full bg-current', meta.pulse && 'animate-pulse-dot')}
        aria-hidden="true"
      />
      {meta.label}
    </Badge>
  );
};

/** Peran anggota dalam organisasi. */
export const ROLE_META = {
  owner: { label: 'Pemilik', tone: 'brand' },
  admin: { label: 'Admin', tone: 'accent' },
  member: { label: 'Anggota', tone: 'neutral' },
  viewer: { label: 'Pengamat', tone: 'neutral' },
};

export const RoleBadge = ({ role, size = 'sm' }) => {
  const meta = ROLE_META[role] ?? { label: role, tone: 'neutral' };
  return (
    <Badge tone={meta.tone} size={size}>
      {meta.label}
    </Badge>
  );
};
